/**
 * Provider-agnostic support (athlete funding) service layer.
 *
 * Ko-fi is only the MVP payment provider. Nothing in the UI should import
 * Ko-fi specifics — swap the provider here (Stripe Connect, Routable, …)
 * without touching profiles, dashboards or the database contract.
 */

export type SupportProvider = "kofi" | "stripe" | "routable" | string;

export type SupportConfig = {
  enabled: boolean;
  provider: SupportProvider;
  paymentUrl: string | null;
};

export const DEFAULT_SUPPORT_BLURB =
  "Be part of her journey. Your support helps this athlete continue putting resources into the training, travel, equipment and support required to compete and grow her career.";

export type SupportableAthlete = {
  support_blurb?: string | null;
  payment_provider?: string | null;
  payment_url?: string | null;
  support_enabled?: boolean | null;
};

export const getSupportConfig = (athlete: SupportableAthlete | null | undefined): SupportConfig => {
  const paymentUrl = athlete?.payment_url?.trim() || null;
  return {
    provider: (athlete?.payment_provider || "kofi") as SupportProvider,
    paymentUrl,
    enabled: Boolean(athlete?.support_enabled && paymentUrl),
  };
};

/** Opens the hosted payment page in a new tab so the supporter keeps SIIS open. */
export const openSupportPage = (config: SupportConfig) => {
  if (!config.paymentUrl) return;
  window.open(config.paymentUrl, "_blank", "noopener,noreferrer");
};

export type SupportTransaction = {
  id: string;
  athlete_id: string | null;
  provider: string;
  supporter_name: string | null;
  supporter_email: string | null;
  amount: number | string;
  currency: string;
  transaction_type: string;
  is_recurring: boolean;
  message: string | null;
  is_public: boolean;
  transaction_timestamp: string;
};

export const toAmount = (value: number | string) =>
  typeof value === "number" ? value : Number(value ?? 0) || 0;

export const formatMoney = (value: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  one_time: "One-time",
  subscription: "Recurring",
  shop_order: "Shop order",
  commission: "Commission",
};

export const transactionTypeLabel = (type: string) =>
  TRANSACTION_TYPE_LABELS[type] ?? type.replace(/_/g, " ");

export type SupportStats = {
  total: number;
  supporters: number;
  average: number;
  thisMonth: number;
  thisYear: number;
  currency: string;
  year: number;
};

export const computeSupportStats = (rows: SupportTransaction[]): SupportStats => {
  const now = new Date();
  const year = now.getFullYear();
  let total = 0;
  let thisMonth = 0;
  let thisYear = 0;
  const supporterKeys = new Set<string>();

  rows.forEach((r) => {
    const amount = toAmount(r.amount);
    total += amount;
    const d = new Date(r.transaction_timestamp);
    if (d.getFullYear() === year) {
      thisYear += amount;
      if (d.getMonth() === now.getMonth()) thisMonth += amount;
    }
    supporterKeys.add((r.supporter_email || r.supporter_name || r.id).toLowerCase());
  });

  return {
    total,
    supporters: supporterKeys.size,
    average: rows.length ? total / rows.length : 0,
    thisMonth,
    thisYear,
    currency: rows[0]?.currency || "USD",
    year,
  };
};

const csvCell = (value: unknown) => {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const downloadTransactionsCsv = (
  rows: SupportTransaction[],
  filename = "support-transactions.csv",
  includeEmail = false,
) => {
  const header = ["Date", "Supporter", ...(includeEmail ? ["Email"] : []), "Transaction type", "Gross amount", "Currency", "Message"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        new Date(r.transaction_timestamp).toISOString().slice(0, 10),
        r.supporter_name || "Anonymous",
        ...(includeEmail ? [r.supporter_email || ""] : []),
        transactionTypeLabel(r.transaction_type),
        toAmount(r.amount).toFixed(2),
        r.currency,
        r.message || "",
      ]
        .map(csvCell)
        .join(","),
    );
  });

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
